import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { describeChange, type Translate } from '@/lib/watchText';
import {
  createCalendarFeed,
  fetchCalendarFeed,
  fetchSectors,
  fetchWorkspaceAlerts,
  markAlertsRead,
  revokeCalendarFeed,
  saveSectors,
  type WorkspaceAlert,
} from '@/services/workspace';

/**
 * What the workspace tells its owner when they look: marked cases on a recent pauta, marked
 * cases and saved searches that moved. Computed on the server, never sent; an item the archive
 * could not be read for is counted and never reported as unchanged.
 */
export function AlertsInbox() {
  const { t, i18n } = useTranslation(['common', 'cade']);
  const queryClient = useQueryClient();
  const { data, isPending, error } = useQuery({
    queryKey: ['workspace', 'alerts'],
    queryFn: () => fetchWorkspaceAlerts(),
  });
  const read = useMutation({
    mutationFn: (keys: string[]) => markAlertsRead(keys),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['workspace', 'alerts'] }),
  });

  if (isPending || error || !data) return null;
  const unreadKeys = data.alerts.filter((a) => !a.read).map((a) => a.key);

  return (
    <section className="space-y-2" data-alerts>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-foreground">
          {t('common:workspace.alerts.title')}{' '}
          <span className="font-normal text-muted-foreground" data-alerts-unread={data.unread}>
            ({t('common:workspace.alerts.unread', { count: data.unread })})
          </span>
        </h2>
        {unreadKeys.length > 0 ? (
          <button
            type="button"
            disabled={read.isPending}
            onClick={() => read.mutate(unreadKeys)}
            className="rounded-md border px-2 py-1 text-xs text-foreground disabled:opacity-50"
          >
            {t('common:workspace.alerts.markAll')}
          </button>
        ) : null}
      </div>
      {data.alerts.length === 0 ? (
        <p className="text-sm text-muted-foreground" role="status">
          {t('common:workspace.alerts.none')}
        </p>
      ) : (
        <ul className="space-y-2">
          {data.alerts.map((alert) => (
            <AlertRow
              key={alert.key}
              alert={alert}
              locale={i18n.language}
              onRead={() => read.mutate([alert.key])}
            />
          ))}
        </ul>
      )}
      {(data.sectors_followed?.length ?? 0) > 0 ? (
        <p className="text-xs text-muted-foreground" data-alerts-sector-as-of>
          {t('common:workspace.alerts.sectorAsOf', { date: data.sector_as_of ?? '—' })}
        </p>
      ) : null}
      {data.unavailable > 0 ? (
        <p className="text-xs text-muted-foreground" data-alerts-unavailable>
          {t('common:workspace.alerts.unavailable', { count: data.unavailable })}
        </p>
      ) : null}
    </section>
  );
}

function PautaBody({ alert, locale }: { alert: WorkspaceAlert; locale: string }) {
  const { t } = useTranslation(['common', 'cade']);
  const date = alert.when
    ? new Intl.DateTimeFormat(locale, { dateStyle: 'long', timeZone: 'UTC' }).format(
        new Date(`${alert.when}T00:00:00Z`),
      )
    : '';
  const kind =
    alert.session_kind === 'EXTRAORDINARY'
      ? t('cade:pautas.extraordinary')
      : t('cade:pautas.ordinary');
  const session =
    alert.session_number !== null
      ? t('cade:pautas.session', { number: alert.session_number, kind })
      : t('cade:pautas.sessionNoNumber', { kind });
  return (
    <>
      <p>
        {t('common:workspace.alerts.pauta', {
          label: alert.label,
          session,
          date,
          standing: alert.standing ? t(`cade:pautas.standing.${alert.standing}`) : '',
        })}{' '}
        {alert.upcoming ? (
          <span className="rounded bg-primary/10 px-1.5 py-0.5 text-xs text-foreground">
            {t('common:workspace.alerts.upcoming')}
          </span>
        ) : null}
      </p>
      <p className="text-xs text-muted-foreground">{t('common:workspace.alerts.pautaNote')}</p>
    </>
  );
}

function ChangeBody({ alert }: { alert: WorkspaceAlert }) {
  const { t } = useTranslation(['common', 'cade']);
  const headline =
    alert.kind === 'case_changed'
      ? 'common:workspace.alerts.caseChanged'
      : 'common:workspace.alerts.searchChanged';
  return (
    <>
      <p>{t(headline, { label: alert.label })}</p>
      <ul className="list-disc pl-5 text-xs">
        {alert.changes.map((change) => (
          <li key={change.field}>{describeChange(change, t as unknown as Translate)}</li>
        ))}
      </ul>
    </>
  );
}

function SectorBody({ alert, locale }: { alert: WorkspaceAlert; locale: string }) {
  const { t } = useTranslation(['common']);
  const date = alert.when
    ? new Intl.DateTimeFormat(locale, { dateStyle: 'long', timeZone: 'UTC' }).format(
        new Date(`${alert.when}T00:00:00Z`),
      )
    : '—';
  const sections = (alert.sections ?? []).join(', ');
  if (alert.kind === 'tcc_signed') {
    return <p>{t('common:workspace.alerts.tccSigned', { label: alert.label, date, sections })}</p>;
  }
  return (
    <>
      <p>
        {t('common:workspace.alerts.acPublished', {
          label: alert.label,
          number: alert.edital_number ?? '—',
          date,
          sections,
        })}
        {alert.nature ? (
          <span className="text-muted-foreground"> ({alert.nature})</span>
        ) : null}
      </p>
      {/* The edital states no deadline: the Regiment sets it. Said every time, never computed. */}
      <p className="text-xs text-muted-foreground">{t('common:workspace.alerts.acDeadline')}</p>
    </>
  );
}

/** Where an alert leads: the case page, or the decision search a saved search was opened with. */
function alertHref(alert: WorkspaceAlert): string | null {
  if (alert.kind === 'search_changed') return `/cade/decisions?${alert.item_key}`;
  const digits = alert.item_key.replace(/\D/g, '');
  return digits ? `/cade/c/${digits}` : null;
}

function AlertRow({
  alert,
  locale,
  onRead,
}: {
  alert: WorkspaceAlert;
  locale: string;
  onRead: () => void;
}) {
  const { t } = useTranslation(['common']);
  const href = alertHref(alert);
  return (
    <li
      className={`space-y-1 rounded-md border p-3 text-sm ${
        alert.read ? 'text-muted-foreground' : 'text-foreground'
      }`}
      data-alert={alert.kind}
      data-alert-read={alert.read}
    >
      {alert.kind === 'pauta' ? (
        <PautaBody alert={alert} locale={locale} />
      ) : alert.kind === 'tcc_signed' || alert.kind === 'ac_published' ? (
        <SectorBody alert={alert} locale={locale} />
      ) : (
        <ChangeBody alert={alert} />
      )}
      <div className="flex items-center gap-3 text-xs">
        {href ? (
          <Link to={href} className="text-primary underline underline-offset-4">
            {t('common:workspace.alerts.open')}
          </Link>
        ) : null}
        {alert.url ? (
          <a
            href={alert.url}
            target="_blank"
            rel="noreferrer"
            className="text-primary underline underline-offset-4"
          >
            {t('common:workspace.alerts.openSei')}
          </a>
        ) : null}
        {alert.read ? null : (
          <button
            type="button"
            onClick={onRead}
            className="text-primary underline underline-offset-4"
          >
            {t('common:workspace.alerts.markRead')}
          </button>
        )}
      </div>
    </li>
  );
}

/**
 * The calendar feed: one secret URL a calendar app subscribes to. The path is shown ONCE, when
 * it is created, because only its hash is stored.
 */
export function CalendarCard() {
  const { t, i18n } = useTranslation(['common']);
  const queryClient = useQueryClient();
  const [shown, setShown] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const { data } = useQuery({
    queryKey: ['workspace', 'calendar'],
    queryFn: () => fetchCalendarFeed(),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['workspace', 'calendar'] });
  const create = useMutation({
    mutationFn: () => createCalendarFeed(),
    onSuccess: (feed) => {
      setShown(feed.path ? `${window.location.origin}${feed.path}` : null);
      setCopied(false);
      refresh();
    },
  });
  const revoke = useMutation({
    mutationFn: () => revokeCalendarFeed(),
    onSuccess: () => {
      setShown(null);
      refresh();
    },
  });
  if (!data) return null;
  const when = (iso: string) => new Date(iso).toLocaleDateString(i18n.language);

  return (
    <section className="space-y-2 rounded-lg border p-3" data-calendar>
      <h2 className="text-sm font-semibold text-foreground">
        {t('common:workspace.calendar.title')}
      </h2>
      <p className="text-xs text-muted-foreground">{t('common:workspace.calendar.explain')}</p>
      {shown ? (
        <div className="space-y-1" data-calendar-url>
          <label className="block text-xs text-muted-foreground" htmlFor="calendar-url">
            {t('common:workspace.calendar.urlLabel')}
          </label>
          <div className="flex gap-2">
            <input
              id="calendar-url"
              readOnly
              value={shown}
              onFocus={(event) => event.target.select()}
              className="min-w-0 flex-1 rounded-md border bg-background px-2 py-1 text-xs text-foreground"
            />
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard?.writeText(shown);
                setCopied(true);
              }}
              className="rounded-md border px-2 py-1 text-xs text-foreground"
            >
              {copied ? t('common:workspace.calendar.copied') : t('common:workspace.calendar.copy')}
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            {t('common:workspace.calendar.shownOnce')}
          </p>
        </div>
      ) : null}
      {data.enabled ? (
        <p className="text-xs text-foreground" data-calendar-active>
          {t('common:workspace.calendar.active', {
            date: data.created_at ? when(data.created_at) : '',
          })}{' '}
          {data.last_read_at
            ? t('common:workspace.calendar.lastRead', { date: when(data.last_read_at) })
            : t('common:workspace.calendar.neverRead')}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={create.isPending}
          onClick={() => create.mutate()}
          className="rounded-md bg-primary px-3 py-1 text-xs text-primary-foreground disabled:opacity-50"
        >
          {data.enabled
            ? t('common:workspace.calendar.rotate')
            : t('common:workspace.calendar.create')}
        </button>
        {data.enabled ? (
          <button
            type="button"
            disabled={revoke.isPending}
            onClick={() => revoke.mutate()}
            className="rounded-md border px-3 py-1 text-xs text-foreground disabled:opacity-50"
          >
            {t('common:workspace.calendar.revoke')}
          </button>
        ) : null}
      </div>
      {data.enabled ? (
        <p className="text-xs text-muted-foreground">{t('common:workspace.calendar.rotateWarn')}</p>
      ) : null}
    </section>
  );
}

/**
 * The CNAE sections this account follows for CADE sector alerts: a TCC signed, a merger made
 * public. Section letters as the published register names them; saved as a whole set.
 */
export function SectorsCard() {
  const { t } = useTranslation(['common']);
  const queryClient = useQueryClient();
  const { data } = useQuery({ queryKey: ['workspace', 'sectors'], queryFn: () => fetchSectors() });
  const [picked, setPicked] = useState<string[] | null>(null);
  const save = useMutation({
    mutationFn: (codes: string[]) => saveSectors(codes),
    onSuccess: () => {
      setPicked(null);
      void queryClient.invalidateQueries({ queryKey: ['workspace'] });
    },
  });
  if (!data || !data.available) return null;
  const current = picked ?? data.following;
  const toggle = (code: string) =>
    setPicked(current.includes(code) ? current.filter((c) => c !== code) : [...current, code]);
  return (
    <section className="space-y-2 rounded-md border p-3" data-sectors>
      <h2 className="text-sm font-semibold text-foreground">
        {t('common:workspace.sectors.title')}
      </h2>
      <p className="text-xs text-muted-foreground">{t('common:workspace.sectors.help')}</p>
      <fieldset className="grid gap-1 sm:grid-cols-2">
        <legend className="sr-only">{t('common:workspace.sectors.title')}</legend>
        {data.options.map((option) => (
          <label key={option.code} className="flex items-start gap-2 text-xs text-foreground">
            <input
              type="checkbox"
              checked={current.includes(option.code)}
              onChange={() => toggle(option.code)}
              className="mt-0.5 h-3.5 w-3.5 rounded border"
            />
            <span>
              <span className="font-medium">{option.code}</span> {option.label}
            </span>
          </label>
        ))}
      </fieldset>
      <button
        type="button"
        disabled={picked === null || save.isPending}
        onClick={() => save.mutate(current)}
        className="rounded-md border px-2 py-1 text-xs text-foreground disabled:opacity-50"
      >
        {t('common:workspace.sectors.save')}
      </button>
    </section>
  );
}

